import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { type FormikHelpers } from 'formik'
import * as React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import { Section } from 'src/tailwind/components/Section'
import BackButton from 'src/views/components/BackButton'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormSubmit } from 'src/views/components/forms/formik/FormSubmit'
import Loader from 'src/views/components/Loader'
import SemesterSelect from 'src/views/includes/SemesterSelect'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import { CourseEditFormContent } from './details/AdministratorCourseEditPage/CourseEditFormContent'
import { CourseSyllabusFormSection } from './details/AdministratorCourseEditPage/CourseSyllabusFormSection'
import {
  courseEditFormValuesToPostAdministratorCoursesBody,
  courseSyllabusFormValuesToAdministratorSyllabusSaveBody,
  splitCourseAddFormValues,
} from './details/AdministratorCourseEditPage/mapModels'
import { type CourseAddFormValues } from './details/AdministratorCourseEditPage/types'
import { useCourseAddPageData } from './details/AdministratorCourseEditPage/useCourseAddPageData'

export default function AdministratorCourseAddPage(): JSX.Element {
  const t = useTranslatable()
  return (
    <SuspenseWrapper title={t('course:add_learning_course')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element {
  const t = useTranslatable()
  const locale = useLocale()
  const navigate = useNavigate()
  const headers = useAuthenticatedHeaders()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeSemester = useActiveSemester()
  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const semIdParam = searchParams.get('semId') ?? ''
  const activeSemIdStr = activeSemester?.id != null ? String(activeSemester.id) : ''

  const { data: semesters } = useApi({
    endpoint: Api.getSemesters,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const futureSemIdStr = React.useMemo(() => {
    const futureSem = semesters?.find((s) => s.isFuture === true)
    return futureSem?.id != null ? String(futureSem.id) : ''
  }, [semesters])

  React.useEffect(() => {
    if (semesters == null) return
    if (futureSemIdStr !== '') {
      if (semIdParam === futureSemIdStr) return
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('semId', futureSemIdStr)
          return next
        },
        { replace: true }
      )
    } else {
      if (semIdParam !== '') return
      if (activeSemIdStr === '') return
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('semId', activeSemIdStr)
          return next
        },
        { replace: true }
      )
    }
  }, [activeSemIdStr, futureSemIdStr, semIdParam, semesters, setSearchParams])

  const resolvedSemesterId = React.useMemo((): number | null => {
    const id = futureSemIdStr !== '' ? futureSemIdStr : semIdParam !== '' ? semIdParam : activeSemIdStr
    if (id === '') return null
    const parsed = parseInt(id, 10)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }, [activeSemIdStr, futureSemIdStr, semIdParam])

  const coursesListPath = `/${locale}/administrator/administration/courses`

  React.useEffect(() => {
    if (isSubmitted) {
      void navigate(coursesListPath)
    }
  }, [coursesListPath, isSubmitted, navigate])

  const { isMainAdministrator } = useAdminAccess()

  const { faculties, degrees, courseDefinitions, courseTypeOptions, creditTypes, initialValues, isLoading } =
    useCourseAddPageData({ headers })

  const onSubmit = React.useCallback(
    async (values: CourseAddFormValues, formikHelpers: FormikHelpers<CourseAddFormValues>) => {
      if (resolvedSemesterId == null) {
        toast.error(t('statement:select_semester'))
        return
      }

      const { course, syllabus } = splitCourseAddFormValues(values)

      let courseBody: Api.postAdministratorCoursesParams['body']
      try {
        courseBody = courseEditFormValuesToPostAdministratorCoursesBody(course, resolvedSemesterId)
      } catch {
        toast.error(t('error:an_error_occurred'))
        return
      }

      try {
        await Api.postAdministratorCourses({
          headers,
          body: { ...courseBody, syllabus: courseSyllabusFormValuesToAdministratorSyllabusSaveBody(syllabus) },
        })

        toast.success(t('common:updated_successfully'))
        setIsSubmitted(true)
        formikHelpers.resetForm()
      } catch (error) {
        toast.error(t('error:an_error_occurred'))
        throw error
      }
    },
    [headers, resolvedSemesterId, t]
  )

  const breadcrumbsItems = [
    {
      page: t('course:learning_courses'),
      path: coursesListPath,
    },
    {
      page: t('course:add_learning_course'),
      path: `/${locale}/administrator/administration/courses/add`,
    },
  ]

  return (
    <Section
      title={t('course:add_learning_course')}
      icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faBook} />}
      breadcrubms={breadcrumbsItems}
      rightElement={<BackButton link={coursesListPath} />}
    >
      <div className="mb-6 mt-2">
        <SemesterSelect disabledPreview />
      </div>
      {isLoading ? (
        <Loader className="m-auto mt-8 flex" />
      ) : resolvedSemesterId == null ? (
        <p className="mt-4 px-2 text-primaryTextColor">{t('statement:select_semester')}</p>
      ) : (
        <Form isConfirmable={!isSubmitted} initialValues={initialValues} onSubmit={onSubmit}>
          <CourseEditFormContent
            headers={headers}
            course={null}
            faculties={faculties ?? []}
            degrees={degrees}
            courseDefinitions={courseDefinitions}
            courseTypeOptions={courseTypeOptions}
            creditTypes={creditTypes}
            isMainAdministrator={isMainAdministrator}
            hideSubmit
          />
          <CourseSyllabusFormSection hideSubmit showFormError={false} />
          <div className="mt-4 min-w-0 rounded-card bg-card px-6 pb-6">
            <FormSubmit
              label={t('common:save')}
              classNames={{
                root: 'w-full flex justify-end border-t border-borderGrey py-4',
                button: 'h-[48px]',
              }}
            />
          </div>
        </Form>
      )}
    </Section>
  )
}
